import centerOfMass from '@turf/center-of-mass';
import { convertClassicPolygons } from '@/components/partials/placeSelection/classicToBuilderSync';
import {
  applyMarkSaved,
  computeBulkFieldUpdates,
  missingRequiredFields,
  missingFieldsMessage,
} from '@/components/partials/placeSelection/placeSelectionSaveHelpers';
import { applyLabelMerge } from '@/components/partials/placeSelection/placeSelectionTagHelpers';

const initialState = () => ({
  isOpen: false,
  resolve: null,
  reject: null,
  selectedPlaces: [],
  activeTab: 'select',
  initialShowReview: false,
  experienceMode: 'old',
  // Per-flow cap on how many places may be selected in the modal. 0 means "no
  // limit" (the default when a caller opens the modal without a cap). Set by
  // openPlaceSelectionModal and cleared on close/reset via initialState.
  maxPlaces: 0,
});

function extractCoords(geometry) {
  if (!geometry || !geometry.type) return [];
  if (geometry.type === 'Point') return [geometry.coordinates];

  const flat = (arr) => (Array.isArray(arr[0]) ? arr.flatMap(flat) : [arr]);

  if (geometry.type === 'Polygon') return flat(geometry.coordinates);
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.flatMap((poly) => flat(poly));
  return [];
}

function getCenterLngLat(place) {
  if (!place) return null;

  if (Array.isArray(place.centerLngLat) && place.centerLngLat.length === 2) {
    return place.centerLngLat;
  }

  const geometry = place.geometry || null;
  if (!geometry) return null;

  if (geometry.type === 'Point' && Array.isArray(geometry.coordinates) && geometry.coordinates.length === 2) {
    return geometry.coordinates;
  }

  try {
    const centerFeature = centerOfMass({ type: 'Feature', geometry, properties: {} });
    const coords = centerFeature?.geometry?.coordinates;
    if (Array.isArray(coords) && coords.length === 2) {
      return coords;
    }
  } catch (error) {
    // fall through to coordinate extraction fallback
  }

  const coords = extractCoords(geometry);
  if (coords.length > 0 && Array.isArray(coords[0]) && coords[0].length === 2) {
    return coords[0];
  }

  return null;
}

function toPlacePayload(place) {
  if (!place) return null;

  const centerLngLat = getCenterLngLat(place);

  return {
    _id: place._id || place.placematrix_id || place.id || `${place._source || place.source || "place"}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    _source: place._source || place.source || 'library',
    name: place.name || place.unique_name || "Unnamed place",
    geometry: place.geometry || null,
    centerLngLat,
    // library fields
    near_poi_id: place.near_poi_id || null,
    placematrix_id: place.placematrix_id || null,
    // Marks a library place as originating from the account's Custom library.
    // Mirrors the backend `isCustomPlace` rule and lets the Review screen group
    // it under "Custom" even when its placematrix_id lacks the `_C_` token.
    custom: place.custom === true,
    country: place.country || null,
    unique_name: place.unique_name || null,
    vp_id: place.vp_id || null,
    address: place.address || null,
    category: place.category || null,
    brand: place.brand || null,
    // Canonical tag field. Accept `tags` as an alias so seeded library
    // payloads (which carry the upstream-normalized `tags`) are preserved.
    labels: Array.isArray(place.labels)
      ? place.labels
      : (Array.isArray(place.tags) ? place.tags : []),
    saveError: place.saveError || null,
    // upload fields
    fileName: place.fileName || null,
    // csv fields (only meaningful when _source === 'csv')
    csvSummary: place.csvSummary || null,
    previewFeatures: Array.isArray(place.previewFeatures) ? place.previewFeatures : [],
    uploadPath: place.uploadPath || null,
    uploadFileName: place.uploadFileName || null,
    // csv radius fields (only meaningful for a Radius CSV upload)
    placeType: place.placeType || null,
    radiusValue: place.radiusValue != null ? place.radiusValue : null,
    radiusUnit: place.radiusUnit || null,
    // csv post-registration processing state. While a registered CSV job is
    // being polled, the entry stays in "Places to Save" with a processing
    // indicator instead of flipping straight to the library.
    csvProcessing: place.csvProcessing === true,
    csvStatusMessage: place.csvStatusMessage || null,
  };
}

// Re-evaluate a single place's Row_Error after its fields change. Clears
// `saveError` when the place now satisfies its Required_Fields, or rewrites it
// to the current missing-fields message when it is still invalid — keeping the
// on-screen error consistent with what a Save would produce (Option B).
//
// Only Saveable_Places carry Required_Fields, so a place with no `saveError`
// and nothing missing is returned untouched (avoids needless re-assignment).
// Non-saveable/library rows never have a `saveError` set here, so they pass
// through unchanged.
function reevaluatePlaceSaveError(place) {
  if (!place) return place;

  const missing = missingRequiredFields(place);
  const nextError = missing.length === 0 ? null : missingFieldsMessage(missing);

  if ((place.saveError || null) === nextError) return place;
  return { ...place, saveError: nextError };
}

export default {
  state: initialState(),
  mutations: {
    openPlaceSelectionModal: (state, payload = {}) => {
      state.isOpen = true;
      state.resolve = payload.resolve || null;
      state.reject = payload.reject || null;
      state.activeTab = payload.activeTab || 'select';
      state.initialShowReview = !!payload.initialShowReview;
      // Normalize the cap to a non-negative integer; anything invalid/absent
      // (undefined, null, NaN, <= 0, non-finite) falls back to 0 = "no limit".
      const cap = Number(payload.maxPlaces);
      state.maxPlaces = Number.isFinite(cap) && cap > 0 ? Math.floor(cap) : 0;
      state.selectedPlaces = Array.isArray(payload.selectedPlaces)
        ? payload.selectedPlaces.map(toPlacePayload).filter(Boolean)
        : [];
    },
    setActiveTab: (state, tab) => {
      state.activeTab = tab;
    },
    setExperienceMode: (state, mode) => {
      state.experienceMode = mode === 'new' ? 'new' : 'old';
    },
    setSelectedPlaces: (state, places = []) => {
      state.selectedPlaces = Array.isArray(places)
        ? places.map(toPlacePayload).filter(Boolean)
        : [];
    },
    addSelectedPlace: (state, place) => {
      const normalized = toPlacePayload(place);
      if (!normalized) return;

      // Enforce the per-flow cap (0 = no limit): never grow past maxPlaces.
      if (state.maxPlaces > 0 && state.selectedPlaces.length >= state.maxPlaces) return;

      const exists = state.selectedPlaces.some((item) => item._id === normalized._id);
      if (exists) return;

      state.selectedPlaces.push(normalized);
    },
    addSelectedPlaces: (state, places = []) => {
      for (const place of places) {
        // Enforce the per-flow cap (0 = no limit): stop once at the limit so a
        // bulk add (e.g. select-all) can only fill up to maxPlaces.
        if (state.maxPlaces > 0 && state.selectedPlaces.length >= state.maxPlaces) break;
        const normalized = toPlacePayload(place);
        if (!normalized) continue;
        const exists = state.selectedPlaces.some((item) => item._id === normalized._id);
        if (!exists) state.selectedPlaces.push(normalized);
      }
    },
    removeSelectedPlace: (state, placeId) => {
      state.selectedPlaces = state.selectedPlaces.filter((place) => place._id !== placeId);
    },
    updateSelectedPlaceName: (state, { id, name }) => {
      const place = state.selectedPlaces.find((p) => p._id === id);
      if (place) place.name = name;
    },
    updateSelectedPlaceFields: (state, payload = {}) => {
      const index = state.selectedPlaces.findIndex((p) => p._id === payload.id);
      if (index === -1) return;

      const place = state.selectedPlaces[index];

      if (Object.prototype.hasOwnProperty.call(payload, 'name')) {
        place.name = payload.name || 'Unnamed place';
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'address')) {
        place.address = payload.address || null;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'category')) {
        place.category = payload.category || null;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'brand')) {
        place.brand = payload.brand || null;
      }

      // Clear or refresh this place's Row_Error now that its fields changed, so
      // a fixed place stops showing an error and a still-invalid one shows the
      // fields that remain missing (Option B). Replace via splice so Vue 2
      // tracks the index write, mirroring the array-replace convention the
      // other mutations here use.
      state.selectedPlaces.splice(index, 1, reevaluatePlaceSaveError(place));
    },
    updateSelectedPlaceGeometry: (state, payload = {}) => {
      const place = state.selectedPlaces.find((p) => p._id === payload.id);
      if (!place || !payload.geometry) return;
      place.geometry = payload.geometry;
      place.centerLngLat = getCenterLngLat(place);
    },
    setSelectedPlaceSaveErrors: (state, payload = {}) => {
      const failedIds = Array.isArray(payload.failedIds) ? new Set(payload.failedIds) : new Set();
      const messagesById = payload.messagesById || {};
      const fallback = payload.message || 'Failed to save this place.';

      state.selectedPlaces = state.selectedPlaces.map((place) => ({
        ...place,
        saveError: failedIds.has(place._id)
          ? (messagesById[place._id] || fallback)
          : null,
      }));
    },
    bulkUpdateSaveableFields: (state, payload = {}) => {
      const updates = computeBulkFieldUpdates(state.selectedPlaces, payload);
      if (!updates.length) return;

      const byId = new Map(updates.map((u) => [u.id, u]));
      state.selectedPlaces = state.selectedPlaces.map((place) => {
        const u = byId.get(place._id);
        if (!u) return place;

        const next = { ...place };
        if (Object.prototype.hasOwnProperty.call(u, 'category')) next.category = u.category;
        if (Object.prototype.hasOwnProperty.call(u, 'brand')) next.brand = u.brand;
        // Re-evaluate the touched place's Row_Error against its new values so a
        // bulk edit clears errors it fixed and narrows the message on rows that
        // are still missing other Required_Fields (Option B).
        return reevaluatePlaceSaveError(next);
      });
    },
    markSelectedPlaceSaved: (state, payload) => {
      state.selectedPlaces = applyMarkSaved(state.selectedPlaces, payload);
    },
    // Set the post-registration processing state on a CSV place so it stays in
    // "Places to Save" with a "still processing" indicator while its library
    // job is polled. `processing: false` clears the indicator (e.g. on a
    // terminal poll outcome), leaving the row otherwise unchanged.
    setCsvPlaceProcessing: (state, { id, processing = true, message = null } = {}) => {
      const place = state.selectedPlaces.find((p) => p._id === id);
      if (!place) return;
      place.csvProcessing = processing === true;
      place.csvStatusMessage = message || null;
    },
    addLabelsToLibraryPlaces: (state, { ids, labels } = {}) => {
      state.selectedPlaces = applyLabelMerge(state.selectedPlaces, { ids, labels });
    },
    closePlaceSelectionModal: (state) => {
      Object.assign(state, initialState());
    },
    // Reset the entire Place Selection slice back to its initial state,
    // including clearing the Builder's selectedPlaces. Committed when a brand
    // new Report or Dataset flow is started (and on cancel) so the Builder does
    // not carry places over from a previous flow. Mirrors the jobRequest
    // slice's resetJobRequest.
    resetPlaceSelection: (state) => {
      Object.assign(state, initialState());
    },
  },
  actions: {
    /**
     * Convert Classic polygon data from jobRequest into Builder Selected Places
     * and add them (de-duplicated by _id). Reads across the session store via
     * rootState. Never throws (Req 6.1). No-op that leaves selectedPlaces
     * unchanged when there is nothing convertible (Req 2.2, 3.5, 6.2).
     *
     * @returns {number} count of places added this run (0 when none).
     */
    syncClassicPolygonsToBuilder({ commit, rootState }) {
      let converted = [];
      try {
        converted = convertClassicPolygons(rootState.jobRequest || {});
      } catch (e) {
        // Defense in depth: the helper is designed never to throw (Req 6.1).
        // eslint-disable-next-line no-console
        console.warn('[classicToBuilderSync] conversion failed', e);
        converted = [];
      }
      if (converted.length) commit('addSelectedPlaces', converted);
      return converted.length;
    },
    /**
     * Switch the Choose Places step into Builder, running the Classic -> Builder
     * sync only on a genuine 'old' -> 'new' transition (Req 1.1, 1.4). Committing
     * setExperienceMode alone never syncs, keeping the mutation pure (Req 1.2, 1.3).
     */
    switchToBuilder({ state, commit, dispatch }) {
      const wasOld = state.experienceMode === 'old';
      commit('setExperienceMode', 'new');
      if (wasOld) dispatch('syncClassicPolygonsToBuilder');
    },
  },
};
